import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useAtomValue } from 'jotai';
import { useLocation } from 'react-router-dom';

import { keywordFilterChipsAtom, visibleFilterChipsAtom } from '@/pages/Search/SearchPage/atoms/searchPageAtoms';
import { useNavigateSearchPageWithParams } from '@/pages/Search/SearchPage/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu, FacetChipOption } from '@/pages/Search/SearchPage/types';
import { getFilterActionByFacetId } from '@/pages/Search/SearchPage/utils/getFilterActionByFacetId';
import FacetTypes from '@/types/search/enums/facetTypes';
import {
    modifyParamsRemovePage,
    modifyParamsRemoveParamByFacetId,
    modifyParamsWhenChecked,
    modifyParamsWithFilterTerm,
    modifyParamsWithRange,
    parseQueryParams,
} from '@/utils/queryParams';
import { urlSafeTitle } from '@/utils/urls';

type Props = {
    className?: string;
    isAllFilters?: boolean;
};

const removeValueFromCommaList = (value: unknown, id: string): string | undefined => {
    const remaining = String(value)
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .filter((entry) => entry !== String(id));
    return remaining.length ? remaining.join(',') : undefined;
};

const removeSubjectLikeParamById = (params: Record<string, unknown>, id: string): Record<string, unknown> => {
    const nextParams: Record<string, unknown> = { ...params };
    for (const [key, value] of Object.entries(nextParams)) {
        if (!/subject/i.test(String(key)) || value === undefined || value === null || value === '') {
            continue;
        }
        const remaining = removeValueFromCommaList(value, id);
        if (remaining) {
            nextParams[key] = remaining;
        } else {
            delete nextParams[key];
        }
    }
    return nextParams;
};

export const SearchFilterChips = ({ className, isAllFilters }: Props) => {
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);

    const navigateWithParams = useNavigateSearchPageWithParams();

    const keywordFilterChips = useAtomValue(keywordFilterChipsAtom);
    const visibleFilterChips = useAtomValue(visibleFilterChipsAtom);

    const onRemoveFilterChip = ({ id, label, value }: FacetChipOption) => {
        let newParams;

        switch (value) {
            case FacetTypes.CURRENT_BID_ID:
                newParams = modifyParamsWithRange(queryParams, FacetTypes.CURRENT_BID_ID, 0, 0);
                newParams = modifyParamsRemoveParamByFacetId(newParams, FacetTypes.CURRENT_BID_ID);
                break;
            case FacetTypes.ESTIMATED_PRICE_ID:
                newParams = modifyParamsWithRange(queryParams, FacetTypes.ESTIMATED_PRICE_ID, 0, 0);
                newParams = modifyParamsRemoveParamByFacetId(newParams, FacetTypes.ESTIMATED_PRICE_ID);
                break;
            case FacetTypes.HAMMER_PRICE_ID:
                newParams = modifyParamsWithRange(queryParams, FacetTypes.HAMMER_PRICE_ID, 0, 0);
                newParams = modifyParamsRemoveParamByFacetId(newParams, FacetTypes.HAMMER_PRICE_ID);
                break;
            case FacetTypes.KEYWORD_ID:
                newParams = modifyParamsWithFilterTerm(queryParams, label);
                break;
            case FacetTypes.ORIGIN_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.ORIGIN_ID, urlSafeTitle(label), false, {
                    useSemanticAttributeParams: true,
                });
                break;

            // TREAT AS OPTIONS
            case FacetTypes.SALE_TYPE_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.SALE_TYPE_ID, id, false);
                break;
            case FacetTypes.AUCTION_HOUSE_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.AUCTION_HOUSE_ID, id, false);
                break;
            case FacetTypes.MATERIALS_AND_TECHNIQUES_ID:
                newParams = modifyParamsWhenChecked(
                    queryParams,
                    FacetTypes.MATERIALS_AND_TECHNIQUES_ID,
                    urlSafeTitle(label),
                    false,
                    {
                        useSemanticAttributeParams: true,
                    }
                );
                break;
            case FacetTypes.STYLE_PERIOD_ID:
                newParams = modifyParamsWhenChecked(
                    queryParams,
                    FacetTypes.STYLE_PERIOD_ID,
                    urlSafeTitle(label),
                    false,
                    {
                        useSemanticAttributeParams: true,
                    }
                );
                break;
            case FacetTypes.CREATOR_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.CREATOR_ID, urlSafeTitle(label), false, {
                    useSemanticAttributeParams: true,
                });
                break;
            case FacetTypes.ART_SUBJECT_ID:
                newParams = modifyParamsWhenChecked(queryParams, FacetTypes.ART_SUBJECT_ID, id, false);
                newParams = removeSubjectLikeParamById(newParams, String(id ?? ''));
                break;
            default:
                break;
        }

        newParams = modifyParamsRemovePage(newParams);

        const browseFacetOptionRemovedId =
            value === FacetTypes.CREATOR_ID ||
            value === FacetTypes.STYLE_PERIOD_ID ||
            value === FacetTypes.MATERIALS_AND_TECHNIQUES_ID ||
            value === FacetTypes.ORIGIN_ID ||
            value === FacetTypes.ART_SUBJECT_ID
                ? id
                : undefined;

        navigateWithParams({
            browseFacetOptionRemovedId,
            checkForPageRemoval: true,
            clearSearchSeoSubjectFromPath: value === FacetTypes.ART_SUBJECT_ID,
            currentOpenMenu: isAllFilters ? CurrentOpenMenu.AllFilters : undefined,
            filterAction: getFilterActionByFacetId({ facetId: value, isRemovingFilter: true }),
            params: newParams,
            preventScrollReset: true,
        });
    };

    if (visibleFilterChips.length === 0) {
        return null;
    }

    return (
        <div className={cn('mb-md flex flex-wrap gap-xs', { '!mb-0 mt-xs': isAllFilters }, className)}>
            {isAllFilters
                ? keywordFilterChips.map((chip) => (
                      <Chip
                          aria-label={`Remove ${chip.label.trim()} filter`}
                          id={`filter-${chip.label}`}
                          key={chip.label}
                          onClose={() => onRemoveFilterChip(chip)}
                          selected
                          style="input"
                      >
                          "{chip.label}"
                      </Chip>
                  ))
                : visibleFilterChips.map((chip) => (
                      <Chip
                          aria-label={`Remove ${chip.label.trim()} filter`}
                          id={`filter-${chip.label}`}
                          key={chip.label}
                          onClose={() => onRemoveFilterChip(chip)}
                          selected
                          style="input"
                      >
                          "{chip.label}"
                      </Chip>
                  ))}
        </div>
    );
};
